import { t } from "@travily/i18n";
import { Alert } from "@ui/components/Alert";
import { ButtonDock } from "@ui/components/ButtonDock/ButtonDock";
import { Dialog } from "@ui/components/Dialog";
import { StandardButton } from "@ui/components/StandardButton/StandardButton";

export interface PayoutsCountry {
  code: string;
  name: string;
}

export interface PayoutsCountryModalProps {
  /** Closes the modal. Wired up by RoutedModal. */
  close: () => void;
  /** Sorted list of Stripe-supported country options. */
  supportedCountries: PayoutsCountry[];
  /** Currently selected country code. */
  country: string;
  onCountryChange: (code: string) => void;
  /** Inline error from the form, if any. */
  error?: string;
  onSubmit: () => void;
  isPending: boolean;
}

export function PayoutsCountryModal({
  close,
  supportedCountries,
  country,
  onCountryChange,
  error,
  onSubmit,
  isPending,
}: PayoutsCountryModalProps) {
  return (
    <>
      <Dialog.Header onClose={close} title={t("components.views.account.payouts_country_modal.title")} />
      <Dialog.Body>
        <div className="flex flex-col gap-6 px-1 pb-2">
          <p className="text-base text-neutral-700">{t("components.views.account.payouts_country_modal.intro")}</p>

          <div className="flex flex-col gap-2">
            <label htmlFor="payouts-country" className="text-sm font-medium text-neutral-900">
              {t("components.views.account.payouts_country_modal.country_label")}
            </label>
            <select
              id="payouts-country"
              name="country"
              value={country}
              onChange={(event) => onCountryChange(event.target.value)}
              className="block w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-base text-neutral-900 outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-200"
              required
            >
              <option value="">{t("components.views.account.payouts_country_modal.country_placeholder")}</option>
              {supportedCountries.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.name}
                </option>
              ))}
            </select>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
          </div>

          <Alert
            size="sm"
            status="info"
            title={t("components.views.account.payouts_country_modal.requirements.title")}
            description={[
              t("components.views.account.payouts_country_modal.requirements.id"),
              t("components.views.account.payouts_country_modal.requirements.bank"),
              t("components.views.account.payouts_country_modal.requirements.duration"),
            ]}
          />
        </div>
      </Dialog.Body>
      <Dialog.Footer>
        <ButtonDock
          primaryAction={
            <StandardButton color="pink" size="44" isDisabled={!country} isPending={isPending} onPress={onSubmit}>
              {t("components.views.account.payouts_country_modal.continue")}
            </StandardButton>
          }
        />
      </Dialog.Footer>
    </>
  );
}
